import { Outlet, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LayoutDashboard, Layers, Package, LogOut, Menu, ChevronLeft, BookOpen, UserCog, ShoppingBag, Mail, Boxes } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetTrigger, SheetContent } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { useState } from 'react';

const sidebarLinks = [
  { to: '/admin', label: 'Tableau de bord', icon: LayoutDashboard, exact: true },
  { to: '/admin/tcg', label: 'TCG', icon: Layers },
  { to: '/admin/extensions', label: 'Extensions', icon: Package },
  { to: '/admin/stock', label: 'Stock TCG', icon: Boxes, id: 'stock' },
  { to: '/admin/commandes', label: 'Commandes', icon: ShoppingBag, id: 'orders' },
  { to: '/admin/series', label: 'Séries BD & Manga', icon: BookOpen, id: 'series' },
  { to: '/admin/emails', label: 'Modèles d\'emails', icon: Mail, id: 'emails' },
  { to: '/admin/compte', label: 'Mon compte', icon: UserCog, id: 'account' },
];

function SidebarNav({ onNavigate }) {
  const location = useLocation();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const isActive = (link) => link.exact ? location.pathname === link.to : location.pathname.startsWith(link.to);

  return (
    <div className="flex flex-col h-full">
      <div className="p-5">
        <Link to="/admin" className="block" onClick={onNavigate} data-testid="admin-logo">
          <img src="/logo-header.png" alt="Plume et Pion" className="h-20 w-auto" data-testid="admin-logo-img" />
          <p className="text-xs text-slate-500 mt-2 font-medium uppercase tracking-widest">Administration</p>
        </Link>
      </div>
      <Separator />
      <nav className="flex-1 p-3 space-y-1">
        {sidebarLinks.map(link => {
          const Icon = link.icon;
          return (
            <Link
              key={link.to}
              to={link.to}
              onClick={onNavigate}
              data-testid={`admin-nav-${link.id || link.label.toLowerCase().replace(/\s/g, '-')}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive(link) ? 'bg-violet-50 text-violet-700' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
            >
              <Icon className="h-4 w-4" />
              {link.label}
            </Link>
          );
        })}
      </nav>
      <div className="p-3">
        <Link to="/" className="flex items-center gap-2 px-3 py-2 text-sm text-slate-500 hover:text-slate-700 transition-colors" data-testid="admin-back-to-site">
          <ChevronLeft className="h-4 w-4" />
          Retour au site
        </Link>
        <button
          onClick={() => { logout(); navigate('/admin/login'); }}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition-colors"
          data-testid="admin-logout-btn"
        >
          <LogOut className="h-4 w-4" />
          Déconnexion
        </button>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const { token, loading } = useAuth();
  const [sheetOpen, setSheetOpen] = useState(false);

  if (loading) return <div className="h-screen flex items-center justify-center"><div className="animate-spin h-8 w-8 border-4 border-violet-600 border-t-transparent rounded-full" /></div>;
  if (!token) return <Navigate to="/admin/login" replace />;

  return (
    <div className="flex h-screen bg-slate-50">
      <aside className="hidden lg:flex w-64 flex-col bg-white border-r border-slate-200 shrink-0">
        <SidebarNav />
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden h-14 bg-white border-b border-slate-200 flex items-center px-4 shrink-0">
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="admin-mobile-menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0">
              <SidebarNav onNavigate={() => setSheetOpen(false)} />
            </SheetContent>
          </Sheet>
          <img src="/logo-header.png" alt="Plume et Pion" className="ml-3 h-9 w-auto" />
          <span className="ml-2 text-xs text-slate-500 font-medium uppercase tracking-widest">Admin</span>
        </header>
        <main className="flex-1 overflow-auto p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
